/**
 * D-EZ-102 — THE MOMENTS THE BROADCAST STOPS FOR.
 *
 * The Big One (a single incident that collects five cars or more), a red flag,
 * rain on the way, a rain delay, a race ended by rain or called off. A full-
 * width card at the turn it happened, listing every car caught up in a wreck
 * and what became of it, with the pit reporter's word on who got through.
 *
 * ⚠ NEVER AHEAD OF THE TAPE. `momentAt` only returns a moment whose turn is on
 * screen (and holds it for two turns after); a wreck on turn 31 is not on this
 * card while the boards show turn 30. Each car's fate is read from the same
 * turn's own result, never from later in the race.
 */

import { useMemo, useState } from 'react';
import type { RaceResult } from '../../domain/race.ts';
import { momentAt, raceMoments, type RaceMoment } from '../../domain/story/raceStory.ts';
import { ArtHistory, pickImage } from '../../domain/art/select.ts';
import { makeRng } from '../../domain/dice.ts';
import type { StoredArtImage } from '../../lib/artStore.ts';
import { useCiteText } from '../../lib/prefs.ts';
import { plainLine } from './plain.ts';

export interface RaceMomentCardProps {
  result: RaceResult;
  /** The turn on screen. */
  cursor: number;
  /** The art library, for a wreck photograph when it has one. */
  library?: readonly StoredArtImage[];
  trackCategory?: string | null;
  seed?: number;
}

const FATE_CLASS: Record<string, string> = {
  out: 'wreck__fate--out',
  injured: 'wreck__fate--out',
  'laps-down': 'wreck__fate--down',
  repaired: 'wreck__fate--ok',
  running: 'wreck__fate--ok',
};

export default function RaceMomentCard({ result, cursor, library = [], trackCategory, seed = 0 }: RaceMomentCardProps) {
  const cite = useCiteText();
  const moments = useMemo(() => raceMoments(result), [result]);
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());
  const moment = momentAt(moments, cursor);
  const key = moment ? `${moment.turn}|${moment.kind}` : '';
  const photo = useMemo(() => (moment ? photoFor(moment, library, trackCategory, seed) : null), [moment, library, trackCategory, seed]);
  if (!moment || dismissed.has(key)) return null;

  return (
    <section className={`wreck wreck--${moment.kind}`} aria-live="polite" aria-label={moment.title}>
      <header className="wreck__head">
        <p className="wreck__kicker">Turn {moment.turn}</p>
        <h3 className="wreck__title">{moment.title}</h3>
        <button
          type="button"
          className="wreck__close"
          onClick={() => setDismissed((d) => new Set([...d, key]))}
          aria-label="Close this card"
        >
          ×
        </button>
      </header>
      <div className="wreck__body">
        {photo && (
          <figure className="wreck__photo">
            <img src={photo.url} alt="" />
          </figure>
        )}
        <div className="wreck__text">
          {moment.lines.map((l) => (
            <p key={l} className="wreck__line">
              {cite(l, plainLine(l))}
            </p>
          ))}
          {moment.incident && (
            <>
              <ul className="wreck__cars" aria-label="Every car involved">
                {moment.incident.cars.map((c) => (
                  <li key={c.driverId} className="wreck__car">
                    <span className="wreck__num">{c.carNumber ? `#${c.carNumber}` : ''}</span>
                    <span className="wreck__name">{c.name}</span>
                    <span className={`wreck__fate ${FATE_CLASS[c.fate] ?? ''}`}>{c.fateText}</span>
                  </li>
                ))}
              </ul>
              <p className="wreck__pit">
                <b>Pit road:</b> {moment.incident.followUp}
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

/** A wreck or weather photograph from the library, if it holds one that fits. */
function photoFor(
  moment: RaceMoment,
  library: readonly StoredArtImage[],
  trackCategory: string | null | undefined,
  seed: number,
): StoredArtImage | null {
  if (library.length === 0) return null;
  const tag =
    moment.kind === 'big-one'
      ? 'wreck.big-one'
      : moment.kind === 'red-flag'
        ? 'rare.red-flag'
        : moment.kind === 'rain-shortened'
          ? 'race.rain-shortened'
          : moment.kind === 'cancelled'
            ? 'race.cancelled'
            : 'rain.delay';
  const pick = pickImage(
    library,
    { momentTag: tag, trackCategory, intensity: 'hot', turn: moment.turn, significance: 100 },
    new ArtHistory(),
    makeRng((seed ^ (moment.turn * 0x9e37)) >>> 0),
  );
  return (pick?.image as StoredArtImage | undefined) ?? null;
}
